import { useEffect } from 'react';
import { EbookTemplate } from '../../../mocks/ebookTemplates';

interface EbookCompareModalProps {
  templates: EbookTemplate[];
  onClose: () => void;
  onUseTemplate: (template: EbookTemplate) => void;
  onPreview: (template: EbookTemplate) => void;
}

const BADGE_STYLES: Record<string, string> = {
  Popular: 'bg-amber-100 text-amber-700 border border-amber-200',
  New: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
  Trending: 'bg-rose-100 text-rose-700 border border-rose-200',
};

const TAG_STYLE_MAP: Record<string, { icon: string; bg: string; text: string }> = {
  Minimal: { icon: 'ri-subtract-line', bg: 'bg-slate-100', text: 'text-slate-600' },
  Bold: { icon: 'ri-contrast-2-line', bg: 'bg-slate-800/10', text: 'text-slate-700' },
  Dark: { icon: 'ri-moon-line', bg: 'bg-slate-800/10', text: 'text-slate-700' },
  Colorful: { icon: 'ri-palette-line', bg: 'bg-pink-50', text: 'text-pink-600' },
  Elegant: { icon: 'ri-star-line', bg: 'bg-amber-50', text: 'text-amber-600' },
  Modern: { icon: 'ri-sparkling-line', bg: 'bg-sky-50', text: 'text-sky-600' },
  Corporate: { icon: 'ri-briefcase-line', bg: 'bg-indigo-50', text: 'text-indigo-600' },
  Creative: { icon: 'ri-magic-line', bg: 'bg-purple-50', text: 'text-purple-600' },
  Clean: { icon: 'ri-leaf-line', bg: 'bg-emerald-50', text: 'text-emerald-600' },
  Warm: { icon: 'ri-sun-line', bg: 'bg-orange-50', text: 'text-orange-600' },
  Academic: { icon: 'ri-graduation-cap-line', bg: 'bg-blue-50', text: 'text-blue-600' },
  Inspiring: { icon: 'ri-heart-line', bg: 'bg-rose-50', text: 'text-rose-600' },
  Technical: { icon: 'ri-code-s-slash-line', bg: 'bg-cyan-50', text: 'text-cyan-600' },
  Futuristic: { icon: 'ri-rocket-line', bg: 'bg-sky-50', text: 'text-sky-600' },
  Artistic: { icon: 'ri-brush-line', bg: 'bg-violet-50', text: 'text-violet-600' },
  Natural: { icon: 'ri-plant-line', bg: 'bg-green-50', text: 'text-green-600' },
  Energetic: { icon: 'ri-flashlight-line', bg: 'bg-amber-50', text: 'text-amber-600' },
  Epic: { icon: 'ri-shield-star-line', bg: 'bg-slate-100', text: 'text-slate-700' },
  Romantic: { icon: 'ri-heart-2-line', bg: 'bg-rose-50', text: 'text-rose-600' },
  Calm: { icon: 'ri-drop-line', bg: 'bg-blue-50', text: 'text-blue-600' },
  Friendly: { icon: 'ri-emotion-happy-line', bg: 'bg-yellow-50', text: 'text-yellow-600' },
};

const INCLUDED_FEATURES = [
  'Full chapter structure & outlines',
  'Professional cover page design',
  'Table of contents template',
  'Chapter intro & summary sections',
  'Visual data page templates',
  'Action plan & CTA sections',
  'Export to PDF & EPUB',
  'Custom branding options',
];

const EXPORT_FORMATS = ['PDF', 'EPUB', 'DOCX', 'Kindle'];

interface CompareRow {
  key: string;
  label: string;
  icon: string;
}

const COMPARE_ROWS: CompareRow[] = [
  { key: 'category', label: 'Genre', icon: 'ri-grid-line' },
  { key: 'chapters', label: 'Chapters', icon: 'ri-book-open-line' },
  { key: 'pages', label: 'Page Count', icon: 'ri-pages-line' },
  { key: 'readTime', label: 'Est. Reading Time', icon: 'ri-time-line' },
  { key: 'rating', label: 'Rating', icon: 'ri-star-line' },
  { key: 'downloads', label: 'Downloads', icon: 'ri-download-line' },
  { key: 'badge', label: 'Badge', icon: 'ri-award-line' },
  { key: 'tags', label: 'Style Tags', icon: 'ri-price-tag-3-line' },
  { key: 'color', label: 'Color Accent', icon: 'ri-palette-line' },
  { key: 'includes', label: "What's Included", icon: 'ri-checkbox-circle-line' },
  { key: 'exports', label: 'Export Formats', icon: 'ri-file-download-line' },
];

function renderCell(key: string, template: EbookTemplate, allTemplates: EbookTemplate[]) {
  if (key === 'category') {
    return (
      <span
        className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold"
        style={{ background: `${template.color}18`, color: template.color }}
      >
        {template.category}
      </span>
    );
  }

  if (key === 'chapters') {
    const max = Math.max(...allTemplates.map(t => t.chapters));
    const pct = Math.min(100, (template.chapters / max) * 100);
    return (
      <div className="flex items-center gap-3">
        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-2 rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, backgroundColor: template.color }}
          />
        </div>
        <span className="text-sm font-extrabold text-slate-800 w-8 text-right whitespace-nowrap">
          {template.chapters}
        </span>
      </div>
    );
  }

  if (key === 'pages') {
    const max = Math.max(...allTemplates.map(t => t.pages));
    const pct = Math.min(100, (template.pages / max) * 100);
    return (
      <div className="flex items-center gap-3">
        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-2 rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, backgroundColor: template.color }}
          />
        </div>
        <span className="text-sm font-extrabold text-slate-800 w-12 text-right whitespace-nowrap">
          {template.pages}p
        </span>
      </div>
    );
  }

  if (key === 'readTime') {
    const hours = Math.round(template.pages / 25);
    const mins = Math.round((template.pages % 25) / 25 * 60);
    return (
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${template.color}15` }}>
          <i className="ri-time-line text-sm" style={{ color: template.color }}></i>
        </div>
        <span className="text-sm font-bold text-slate-800">
          {hours > 0 ? `${hours}h` : ''}{mins > 0 && hours === 0 ? `${mins}m` : ''}{hours > 0 && mins > 0 ? ` ${mins}m` : ''}
        </span>
        <span className="text-xs text-slate-400">to read</span>
      </div>
    );
  }

  if (key === 'rating') {
    const stars = Math.round(template.rating * 2) / 2;
    return (
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-0.5">
          {[1, 2, 3, 4, 5].map(n => {
            const filled = n <= Math.floor(stars);
            const half = !filled && n - 0.5 === stars;
            return (
              <i
                key={n}
                className={`text-base ${filled ? 'ri-star-fill text-amber-400' : half ? 'ri-star-half-line text-amber-400' : 'ri-star-line text-slate-200'}`}
              ></i>
            );
          })}
        </div>
        <span className="text-sm font-extrabold text-slate-800">{template.rating}</span>
      </div>
    );
  }

  if (key === 'downloads') {
    const max = Math.max(...allTemplates.map(t => t.downloads));
    const pct = Math.min(100, (template.downloads / max) * 100);
    return (
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <i className="ri-download-line text-emerald-500 text-sm"></i>
          <span className="text-sm font-extrabold text-slate-800">{template.downloads.toLocaleString()}</span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-1.5 rounded-full bg-emerald-400 transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    );
  }

  if (key === 'badge') {
    return template.badge ? (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${BADGE_STYLES[template.badge]}`}>
        <i className={template.badge === 'Popular' ? 'ri-fire-line' : template.badge === 'New' ? 'ri-sparkling-line' : 'ri-trending-up-line'}></i>
        {template.badge}
      </span>
    ) : (
      <span className="text-xs text-slate-300 italic font-medium">No badge</span>
    );
  }

  if (key === 'tags') {
    return (
      <div className="flex flex-wrap gap-1.5">
        {template.tags.map(tag => {
          const s = TAG_STYLE_MAP[tag] || { icon: 'ri-price-tag-3-line', bg: 'bg-slate-100', text: 'text-slate-500' };
          return (
            <div key={tag} className={`flex items-center gap-1 px-2 py-1 rounded-lg ${s.bg}`}>
              <i className={`${s.icon} text-xs ${s.text}`}></i>
              <span className={`text-xs font-semibold ${s.text} whitespace-nowrap`}>{tag}</span>
            </div>
          );
        })}
      </div>
    );
  }

  if (key === 'color') {
    return (
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-xl border-2 border-white shadow-md flex-shrink-0"
          style={{ backgroundColor: template.color }}
        />
        <div>
          <p className="text-xs font-bold text-slate-800">{template.color.toUpperCase()}</p>
          <p className="text-xs text-slate-400">Primary accent</p>
        </div>
      </div>
    );
  }

  if (key === 'includes') {
    return (
      <div className="space-y-1.5">
        {INCLUDED_FEATURES.map(feature => (
          <div key={feature} className="flex items-start gap-2">
            <i className="ri-checkbox-circle-fill text-emerald-500 text-sm flex-shrink-0 mt-0.5"></i>
            <span className="text-xs text-slate-600 leading-snug">{feature}</span>
          </div>
        ))}
      </div>
    );
  }

  if (key === 'exports') {
    return (
      <div className="flex flex-wrap gap-2">
        {EXPORT_FORMATS.map(fmt => (
          <div
            key={fmt}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-bold"
            style={{ borderColor: `${template.color}40`, color: template.color, background: `${template.color}0c` }}
          >
            <i className="ri-file-download-line text-sm"></i>
            {fmt}
          </div>
        ))}
      </div>
    );
  }

  return <span className="text-sm text-slate-700">—</span>;
}

export default function EbookCompareModal({ templates, onClose, onUseTemplate, onPreview }: EbookCompareModalProps) {
  const colClass = templates.length === 2 ? 'w-1/2' : 'w-1/3';
  const allTemplates = templates; // for relative bar calculations

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden">

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-md shadow-amber-200">
              <i className="ri-layout-column-line text-white text-xl"></i>
            </div>
            <div>
              <h2 className="text-xl font-extrabold text-slate-900">Compare Templates</h2>
              <p className="text-xs text-slate-400 mt-0.5">
                Side-by-side comparison · {templates.length} templates selected
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-xl border border-slate-200 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-all cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* ── Body ───────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">

          {/* Sticky template header cards */}
          <div className="flex border-b border-slate-100 sticky top-0 bg-white z-10 shadow-sm">
            {/* Label column */}
            <div className="w-44 flex-shrink-0 px-6 py-5 flex items-end">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Feature</span>
            </div>

            {templates.map(t => (
              <div key={t.id} className={`${colClass} flex-shrink-0 px-5 py-4 border-l border-slate-100`}>
                {/* Cover image */}
                <div
                  className="relative rounded-2xl overflow-hidden mb-3 cursor-pointer group"
                  style={{ aspectRatio: '3/4', maxHeight: '160px' }}
                  onClick={() => onPreview(t)}
                >
                  <img
                    src={t.image}
                    alt={t.name}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <div className="bg-white/90 rounded-lg px-2.5 py-1.5 flex items-center gap-1.5">
                      <i className="ri-eye-line text-slate-700 text-xs"></i>
                      <span className="text-xs font-bold text-slate-700">Preview</span>
                    </div>
                  </div>
                  {/* Bottom color bar */}
                  <div className="absolute bottom-0 left-0 right-0 h-1" style={{ backgroundColor: t.color }} />
                  {t.badge && (
                    <div className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-bold ${BADGE_STYLES[t.badge]}`}>
                      {t.badge}
                    </div>
                  )}
                  {/* Stats overlay bottom */}
                  <div className="absolute bottom-2 right-2 flex items-center gap-1 px-1.5 py-0.5 bg-black/50 rounded-full">
                    <i className="ri-star-fill text-amber-400 text-xs"></i>
                    <span className="text-white text-xs font-bold">{t.rating}</span>
                  </div>
                </div>

                <h3 className="text-sm font-extrabold text-slate-900 line-clamp-2 leading-snug mb-1">
                  {t.name}
                </h3>
                <p className="text-xs text-slate-400 line-clamp-2 leading-relaxed">
                  {t.description}
                </p>
              </div>
            ))}
          </div>

          {/* Comparison rows */}
          {COMPARE_ROWS.map((row, idx) => (
            <div
              key={row.key}
              className={`flex border-b border-slate-50 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}
            >
              {/* Row label */}
              <div className="w-44 flex-shrink-0 px-6 py-4 flex items-start gap-2.5">
                <div className="w-6 h-6 flex items-center justify-center mt-0.5 flex-shrink-0">
                  <i className={`${row.icon} text-slate-400 text-sm`}></i>
                </div>
                <span className="text-xs font-bold text-slate-500 leading-snug mt-0.5">{row.label}</span>
              </div>

              {/* Values */}
              {templates.map(t => (
                <div key={t.id} className={`${colClass} flex-shrink-0 px-5 py-4 border-l border-slate-100`}>
                  {renderCell(row.key, t, allTemplates)}
                </div>
              ))}
            </div>
          ))}

          {/* Win/recommendation row */}
          <div className="flex border-b border-slate-100 bg-gradient-to-r from-amber-50/50 to-orange-50/30">
            <div className="w-44 flex-shrink-0 px-6 py-5 flex items-start gap-2.5">
              <div className="w-6 h-6 flex items-center justify-center mt-0.5 flex-shrink-0">
                <i className="ri-trophy-line text-amber-500 text-sm"></i>
              </div>
              <span className="text-xs font-bold text-slate-500 leading-snug mt-0.5">OpenClaw Pick</span>
            </div>
            {(() => {
              const best = [...templates].sort((a, b) => {
                const scoreA = a.rating * 10 + Math.log10(a.downloads + 1) + (a.badge === 'Popular' ? 5 : a.badge === 'Trending' ? 3 : a.badge === 'New' ? 1 : 0);
                const scoreB = b.rating * 10 + Math.log10(b.downloads + 1) + (b.badge === 'Popular' ? 5 : b.badge === 'Trending' ? 3 : b.badge === 'New' ? 1 : 0);
                return scoreB - scoreA;
              })[0];

              return templates.map(t => (
                <div key={t.id} className={`${colClass} flex-shrink-0 px-5 py-5 border-l border-slate-100 flex items-center`}>
                  {t.id === best.id ? (
                    <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border-2 border-amber-400 bg-amber-50">
                      <i className="ri-trophy-fill text-amber-500 text-lg flex-shrink-0"></i>
                      <div>
                        <p className="text-xs font-extrabold text-amber-800">Top Recommendation</p>
                        <p className="text-xs text-amber-600 mt-0.5">Best rating + downloads combo</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-slate-400">
                      <i className="ri-checkbox-blank-circle-line text-sm"></i>
                      <span className="text-xs font-medium">Good choice</span>
                    </div>
                  )}
                </div>
              ));
            })()}
          </div>
        </div>

        {/* ── Footer CTAs ─────────────────────────────────────────────── */}
        <div className="flex border-t border-slate-100 flex-shrink-0 bg-white">
          {/* Back label */}
          <div className="w-44 flex-shrink-0 px-6 py-4 flex items-center">
            <button
              onClick={onClose}
              className="text-xs font-semibold text-slate-400 hover:text-slate-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-arrow-left-line text-sm"></i>
              Close
            </button>
          </div>

          {templates.map(t => (
            <div key={t.id} className={`${colClass} flex-shrink-0 px-5 py-4 border-l border-slate-100`}>
              <button
                onClick={() => onUseTemplate(t)}
                className="w-full py-3 rounded-xl text-sm font-extrabold text-white transition-all hover:opacity-90 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow-lg"
                style={{ background: `linear-gradient(135deg, ${t.color}, ${t.color}cc)` }}
              >
                <i className="ri-edit-line"></i>
                Use This Template
              </button>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}
